'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { Menu } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { cn } from '@/lib/utils';
import { activeVehicleTypes } from '@/data/vehicles';

// Helper to generate URL-friendly type param
const typeToParam = (type: string) => type.toLowerCase().replace(/\s+/g, '');

// Generate vehicle dropdown items dynamically based on active vehicle types
const vehicleDropdownItems = [
  { label: 'All Vehicles', href: '/vehicles', description: 'Browse our complete fleet' },
  ...activeVehicleTypes.map(type => ({
    label: type + 's',
    href: `/vehicles?type=${typeToParam(type)}`,
    description: type === 'Scooter' ? 'Easy to ride, perfect for beginners' :
      type === 'Motor Bike' ? 'For the adventure seekers' :
        type === 'Car' ? 'Comfortable travel for families' :
          type === 'Tuk Tuk' ? 'The iconic Sri Lankan experience' :
            type === 'Bicycle' ? 'Eco-friendly exploration' : '',
  })),
];

const navItems = [
  { label: 'Home', href: '/' },
  {
    label: 'Vehicles',
    href: '/vehicles',
    dropdown: vehicleDropdownItems,
  },
  {
    label: 'Services',
    href: '/services',
    dropdown: [
      { label: 'All Services', href: '/services', description: 'View all our services' },
      { label: 'Driving Permit', href: '/driving-permit', description: 'Get your temporary license' },
      { label: 'Pickup & Delivery', href: '/services#pickup', description: 'Island-wide service' },
    ],
  },
  { label: 'Blog', href: '/blog' },
  { label: 'About', href: '/about' },
  { label: 'Contact us', href: '/contact' },
];


export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header
      className={cn(
        'fixed top-0 left-0 w-full z-50 transition-all duration-300',
        isScrolled
          ? 'bg-gradient-to-r from-[#1a1510]/98 via-[#0f0f1a]/98 to-[#1a1510]/98 shadow-lg backdrop-blur-md'
          : 'bg-gradient-to-r from-[#1a1510]/95 via-[#0f0f1a]/95 to-[#1a1510]/95 backdrop-blur-sm'
      )}
    >
      <div className="max-w-7xl mx-auto px-6 py-5 flex justify-between items-center">
        <Link href="/" className="flex items-center">
          <span className="font-coconat text-2xl text-[hsl(var(--accent))]">Ceylon Bike Rent</span>
        </Link>

        {/* Desktop Navigation - Warm Elegant Style */}
        <nav className="hidden lg:flex items-center gap-8">
          {navItems.map((item) => (
            item.dropdown ? (
              <NavigationMenu key={item.label}>
                <NavigationMenuList>
                  <NavigationMenuItem>
                    <NavigationMenuTrigger
                      variant="ghost"
                      className="text-white/50 hover:text-[hsl(var(--accent))] data-[state=open]:text-[hsl(var(--accent))] p-0 h-auto font-normal text-sm"
                    >
                      {item.label}
                    </NavigationMenuTrigger>
                    <NavigationMenuContent>
                      <ul className="grid w-[400px] gap-1 p-4 bg-[#1a1510] border border-[hsl(var(--accent))]/20">
                        {item.dropdown.map((subItem) => (
                          <li key={subItem.label}>
                            <NavigationMenuLink asChild>
                              <Link
                                href={subItem.href}
                                className="group block select-none rounded-lg p-3 leading-none no-underline outline-none transition-colors hover:bg-white/5"
                              >
                                <div className="text-sm font-medium leading-none mb-1 text-white/80 group-hover:text-[hsl(var(--accent))] transition-colors">
                                  {subItem.label}
                                </div>
                                {subItem.description && (
                                  <p className="line-clamp-2 text-xs leading-snug text-white/50 group-hover:text-white/60 transition-colors">
                                    {subItem.description}
                                  </p>
                                )}
                              </Link>
                            </NavigationMenuLink>
                          </li>
                        ))}
                      </ul>
                    </NavigationMenuContent>
                  </NavigationMenuItem>
                </NavigationMenuList>
              </NavigationMenu>
            ) : (
              <Link
                key={item.label}
                href={item.href}
                className="text-sm text-white/50 hover:text-[hsl(var(--accent))] transition-colors"
              >
                {item.label}
              </Link>
            )
          ))}
        </nav>

        {/* Desktop Book Now Button - Keep Primary Style */}
        <Button asChild className="hidden lg:flex">
          <Link href="/book">Book Now</Link>
        </Button>

        {/* Mobile Menu - Warm Elegant Style */}
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="lg:hidden text-[hsl(var(--accent))] hover:bg-transparent hover:text-[hsl(var(--accent))]">
              <Menu className="h-6 w-6" />
              <span className="sr-only">Toggle menu</span>
            </Button>
          </SheetTrigger>
          <SheetContent side="right" className="w-[300px] bg-gradient-to-b from-[#1a1510] to-[#0f0f1a] border-l border-[hsl(var(--accent))]/20">
            <SheetHeader>
              <SheetTitle className="text-left text-[hsl(var(--accent))] font-coconat text-xl">Menu</SheetTitle>
            </SheetHeader>
            <nav className="mt-8">
              <Accordion type="single" collapsible className="w-full">
                {navItems.map((item, index) => (
                  item.dropdown ? (
                    <AccordionItem key={item.label} value={`item-${index}`} className="border-b-0">
                      <AccordionTrigger className="text-white/50 hover:text-[hsl(var(--accent))] hover:no-underline py-3">
                        {item.label}
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="flex flex-col space-y-2 pl-4">
                          {item.dropdown.map((subItem) => (
                            <Link
                              key={subItem.label}
                              href={subItem.href}
                              onClick={() => setIsOpen(false)}
                              className="text-sm text-white/40 hover:text-[hsl(var(--accent))] py-2 transition-colors"
                            >
                              {subItem.label}
                            </Link>
                          ))}
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ) : (
                    <div key={item.label}>
                      <Link
                        href={item.href}
                        onClick={() => setIsOpen(false)}
                        className="flex py-3 text-white/50 hover:text-[hsl(var(--accent))] transition-colors"
                      >
                        {item.label}
                      </Link>
                    </div>
                  )
                ))}
              </Accordion>

              {/* Mobile Book Now Button - Keep Primary Style */}
              <div className="mt-8">
                <Button asChild className="w-full">
                  <Link href="/book" onClick={() => setIsOpen(false)}>
                    Book Now
                  </Link>
                </Button>
              </div>
            </nav>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
}
